import React, { useState, useEffect } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import { Badge } from "../../../components/ui/badge"
import { Checkbox } from "../../../components/ui/Checkbox"
import { listCourseEnrollments, assignCohort } from "../../../utils/enrollmentHelpers"

// Absorbs the bulk-assign functionality that used to live in CohortsPanel.jsx.
// Shows every enrollment in this cohort's course (not just already-assigned
// learners) so an admin can pull learners in from "unassigned" or move them
// from another cohort on the same course. A learner can only sit in one
// cohort per course (single cohortId field on the enrollment doc) â€” toggling
// on overwrites any previous cohort assignment for that enrollment.
const CohortLearnersTab = ({ orgId, cohortId, courseId, cohortName }) => {
  const [enrollments, setEnrollments] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [pendingUserId, setPendingUserId] = useState(null)

  const load = async () => {
    setLoading(true)
    const { enrollments: list } = await listCourseEnrollments(orgId, courseId)
    setEnrollments(list || [])
    setLoading(false)
  }

  useEffect(() => {
    if (orgId && courseId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, courseId])

  const handleToggle = async (enrollment, isAssigning) => {
    setPendingUserId(enrollment?.userId)
    await assignCohort(enrollment?.userId, courseId, {
      cohortId: isAssigning ? cohortId : "",
      cohortName: isAssigning ? cohortName : "",
    })
    setPendingUserId(null)
    load()
  }

  const filtered = enrollments?.filter((e) => {
    const q = search.toLowerCase()
    if (!q) return true
    return (e?.learnerName || "")?.toLowerCase()?.includes(q) || (e?.learnerEmail || "")?.toLowerCase()?.includes(q)
  })

  const assignedCount = enrollments?.filter((e) => e?.cohortId === cohortId)?.length || 0

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading learners...</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">{assignedCount} of {enrollments?.length || 0} enrolled learners assigned to this cohort</p>
        <div className="w-64">
          <Input placeholder="Search learners..." value={search} onChange={(e) => setSearch(e?.target?.value)} />
        </div>
      </div>

      {filtered?.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Icon name="Users" size={24} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No learners enrolled in this course yet.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Learner</th>
                <th className="text-left px-4 py-3 font-medium">Progress</th>
                <th className="text-left px-4 py-3 font-medium">Current Cohort</th>
                <th className="text-right px-4 py-3 font-medium">Assigned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered?.map((e) => {
                const isAssigned = e?.cohortId === cohortId
                const isElsewhere = e?.cohortId && !isAssigned
                return (
                  <tr key={e?.userId} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{e?.learnerName || "Unnamed learner"}</p>
                      <p className="text-xs text-muted-foreground">{e?.learnerEmail || e?.userId}</p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{e?.progressPercent || 0}%</td>
                    <td className="px-4 py-3">
                      {isElsewhere ? (
                        <Badge variant="secondary">{e?.cohortName || "Another cohort"}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>
                                        <td className="px-4 py-3 text-right">
                      <div className="flex justify-end">
                        <Checkbox
                          checked={isAssigned}
                          disabled={pendingUserId === e?.userId}
                          onChange={(ev) => handleToggle(e, ev?.target?.checked)}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default CohortLearnersTab